import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { AR_OBJECTS } from '../content';
import { clamp01, easeInOut } from '../film/math';

/** The parts of Google's <model-viewer> element this section uses (https://modelviewer.dev). */
interface ModelViewer extends HTMLElement {
  src: string | null;
  alt: string | null;
  cameraOrbit: string;
  autoRotate: boolean;
  jumpCameraToGoal(): void;
  resetTurntableRotation(theta?: number): void;
  /** where a point of the screen (client coordinates) meets the model; null if it misses the chair */
  positionAndNormalFromPoint(x: number, y: number): unknown | null;
}

/** How a device opens AR: inside the page, in Google's viewer app, in Apple's, or not at all. */
type ArMode = 'webxr' | 'scene-viewer' | 'quick-look' | 'none';

/** the viewer library: a copy in public/, so the section needs no CDN */
const VIEWER_SCRIPT = 'assets/vendor/model-viewer.min.js';
/**
 * Of each step of the scroll, the share spent sliding from one chair to the
 * next. For the rest of it the strip stands still, so every chair has a stop.
 */
const SLIDE = 0.56;
/** how tightly the strip follows the scroll (higher = tighter) */
const FOLLOW = 10;
/** the scroll has stopped once it has been still for this long (ms)… */
const SNAP_AFTER = 180;
/** …and the strip then carries on to the next chair if it is at least this far on its way there */
const SNAP_FROM = 0.1;
/** how faint the small boxes are: those still to come (right), and those already passed (left) */
const NEXT = 0.6;
const DIM = 0.4;
/** the model starts turning by itself this long after it appears */
const SPIN_AFTER = 1500;
const MODEL_FAILED = 'The 3D model could not be loaded. Check the internet connection and try again.';

/**
 * "Try it in your own room": a strip of boxes, one per chair, that slides
 * sideways as the page scrolls (or is dragged). The chair in the middle fills
 * the big box as a live 3D model — drag it round, or stand it in the room
 * through the phone's camera (AR).
 *
 * The section is taller than the screen and its content sticks to the screen
 * while it passes, so scrolling down is what moves the strip along.
 *
 * The 3D and AR are Google's <model-viewer>; it is fetched only once the
 * section is about to scroll into view.
 */
@Component({
  selector: 'app-ar',
  templateUrl: './ar.html',
  styleUrl: './ar.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  // <model-viewer> is a custom element Angular knows nothing about
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  host: {
    '(keydown.arrowLeft)': 'onArrow($event, -1)',
    '(keydown.arrowRight)': 'onArrow($event, 1)',
  },
})
export class Ar {
  protected readonly objects = AR_OBJECTS;

  /** index of the chair in the big box */
  protected readonly active = signal(0);
  /** "02 / 03" */
  protected readonly count = computed(() => {
    const two = (n: number) => String(n).padStart(2, '0');
    return `${two(this.active() + 1)} / ${two(this.objects.length)}`;
  });
  /** the strip has come to rest on that chair */
  private readonly settled = signal(true);
  /** that chair's 3D model has arrived */
  private readonly loaded = signal(false);
  /** the big box shows the live model rather than the picture */
  protected readonly live = computed(() => this.settled() && this.loaded());
  /** the model turns by itself */
  protected readonly spinning = signal(true);
  /** the visitor has turned the model themselves, so the hint has done its job */
  protected readonly touched = signal(false);
  /** the strip is being dragged */
  protected readonly dragging = signal(false);
  /** the price box is showing: the visitor tapped the chair */
  protected readonly priceOpen = signal(false);
  /** the chair in the big box, with its price worked out */
  protected readonly current = computed(() => {
    const object = this.objects[this.active()];
    const amount = (text: string) => Number(text.replace(/[^\d]/g, ''));
    return { ...object, off: Math.round((1 - amount(object.price) / amount(object.mrp)) * 100) };
  });
  /** how this device opens AR — not known until the first model has loaded */
  protected readonly arMode = signal<ArMode | null>(null);
  /** what to do to see the chair in the room, for this device */
  protected readonly arText = signal('');
  /** something went wrong (no viewer, no model, AR refused to start) */
  protected readonly notice = signal('');
  /** the QR code that opens this chair on a phone, as an SVG path in a `size` × `size` grid */
  protected readonly qr = signal<{ size: number; path: string } | null>(null);

  private readonly host = viewChild.required<ElementRef<HTMLElement>>('host');
  private readonly pin = viewChild.required<ElementRef<HTMLElement>>('pin');
  private readonly strip = viewChild.required<ElementRef<HTMLElement>>('strip');
  private readonly frame = viewChild.required<ElementRef<HTMLElement>>('frame');
  private readonly viewerRef = viewChild.required<ElementRef<ModelViewer>>('viewer');

  private thumbs: HTMLElement[] = [];
  private slides: HTMLElement[] = [];
  /** px from the middle of the screen to the middle of the first box beside the frame, and from one box to the next */
  private first = 0;
  private pitch = 0;
  /** where the strip is, in chairs: 1.5 = halfway between the second and the third */
  private t = 0;
  private raf = 0;
  private last = 0;
  private reducedMotion = false;

  /** the viewer, once its library has loaded */
  private viewer: ModelViewer | null = null;
  /** index of the chair whose model the viewer holds (−1 = none yet) */
  private shown = -1;
  private resets = 0;
  private spinTimer = 0;
  private warmed = false;
  private drag: { id: number; x: number; moved: number } | null = null;
  private draggedAt = 0;
  /** which way the page last scrolled: 1 = down */
  private heading = 1;
  /** a finger is on the screen */
  private touching = false;
  private snapTimer = 0;
  private destroyed = false;
  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    // the model comes into view: put it back at the angle of its picture, and let it start turning in a moment
    effect(() => {
      const live = this.live();
      untracked(() => this.onLive(live));
    });
    effect(() => {
      const spinning = this.spinning();
      untracked(() => {
        if (this.viewer && this.live()) this.viewer.autoRotate = spinning;
      });
    });

    afterNextRender(() => this.start());
    // (this also runs where the page is prerendered, which has no window: browser clean-up is in start())
    this.destroyRef.onDestroy(() => (this.destroyed = true));
  }

  // ── set-up ──────────────────────────────────────────────────────────

  private start(): void {
    const host = this.host().nativeElement;
    const pin = this.pin().nativeElement;
    const strip = this.strip().nativeElement;
    this.thumbs = [...host.querySelectorAll<HTMLElement>('.thumb')];
    this.slides = [...host.querySelectorAll<HTMLElement>('.slide')];
    this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (this.reducedMotion) this.spinning.set(false);

    const resized = new ResizeObserver(() => {
      this.measure();
      this.update(true);
    });
    resized.observe(pin);

    let lastY = window.scrollY;
    const onScroll = () => {
      if (window.scrollY !== lastY) this.heading = Math.sign(window.scrollY - lastY);
      lastY = window.scrollY;
      this.wake();
      this.planSnap();
    };
    window.addEventListener('scroll', onScroll, { passive: true });

    // a finger resting on the screen is still scrolling: the strip waits for it to lift before it settles
    const onTouch = (e: TouchEvent) => {
      this.touching = e.touches.length > 0;
      if (!this.touching) this.planSnap();
    };
    for (const type of ['touchstart', 'touchend', 'touchcancel'] as const) {
      window.addEventListener(type, onTouch, { passive: true });
    }

    // a sideways swipe on a trackpad moves the strip too
    const onWheel = (e: WheelEvent) => {
      if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return;
      e.preventDefault();
      window.scrollBy({ top: e.deltaX, behavior: 'instant' });
    };
    pin.addEventListener('wheel', onWheel, { passive: false });

    strip.addEventListener('pointerdown', this.onDragStart);
    strip.addEventListener('pointermove', this.onDragMove);
    strip.addEventListener('pointerup', this.onDragEnd);
    strip.addEventListener('pointercancel', this.onDragEnd);

    // fetch the viewer and the first model only when the section is about to scroll into view
    const near = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        near.disconnect();
        void this.boot();
      },
      { rootMargin: '900px 0px' },
    );
    near.observe(host);

    this.destroyRef.onDestroy(() => {
      cancelAnimationFrame(this.raf);
      clearTimeout(this.spinTimer);
      clearTimeout(this.snapTimer);
      resized.disconnect();
      near.disconnect();
      window.removeEventListener('scroll', onScroll);
      for (const type of ['touchstart', 'touchend', 'touchcancel'] as const) window.removeEventListener(type, onTouch);
      pin.removeEventListener('wheel', onWheel);
      strip.removeEventListener('pointerdown', this.onDragStart);
      strip.removeEventListener('pointermove', this.onDragMove);
      strip.removeEventListener('pointerup', this.onDragEnd);
      strip.removeEventListener('pointercancel', this.onDragEnd);
    });

    this.measure();
    this.update(true);

    // a link made for one chair (the QR code's) opens on that chair
    const wanted = this.objects.findIndex((o) => o.id === new URLSearchParams(location.search).get('ar'));
    if (wanted >= 0) requestAnimationFrame(() => this.scrollToStop(wanted, 'instant'));
  }

  /** Box sizes come from the stylesheet, which changes them with the screen. */
  private measure(): void {
    const host = this.host().nativeElement;
    const gap = parseFloat(getComputedStyle(host).getPropertyValue('--gap')) || 16;
    const width = this.thumbs[0]?.offsetWidth ?? 0;
    this.first = this.frame().nativeElement.offsetWidth / 2 + gap + width / 2;
    this.pitch = width + gap;
  }

  // ── the strip follows the scroll ────────────────────────────────────

  /** How far the scroll has carried the section past the screen, in px, and how far it can. */
  private travel(): { done: number; total: number } {
    const box = this.host().nativeElement.getBoundingClientRect();
    return { done: -box.top, total: box.height - this.pin().nativeElement.offsetHeight };
  }

  /** Where the scroll says the strip should be. */
  private goal(): number {
    const steps = this.objects.length - 1;
    const { done, total } = this.travel();
    if (steps < 1 || total <= 0) return 0;
    const raw = clamp01(done / total) * steps;
    const index = Math.min(steps - 1, Math.floor(raw));
    // hold on a chair, slide to the next, hold again
    return index + easeInOut(clamp01((raw - index - (1 - SLIDE) / 2) / SLIDE));
  }

  private wake(): void {
    if (this.raf || this.destroyed) return;
    this.last = performance.now();
    this.raf = requestAnimationFrame(this.tick);
  }

  private readonly tick = (now: number): void => {
    this.raf = 0;
    const dt = Math.min(0.05, Math.max(0, (now - this.last) / 1000));
    this.last = now;
    if (!this.update(false, dt)) this.raf = requestAnimationFrame(this.tick);
  };

  /** Moves the strip towards where the scroll says it should be. True once it is there. */
  private update(snap: boolean, dt = 0): boolean {
    const goal = this.goal();
    // (a drag is followed exactly: easing would make the strip lag behind the finger)
    const jump = snap || this.reducedMotion || this.dragging();
    this.t = jump ? goal : this.t + (goal - this.t) * (1 - Math.exp(-dt * FOLLOW));
    if (Math.abs(goal - this.t) < 0.0005) this.t = goal;
    this.place();
    return this.t === goal;
  }

  /** Runs every frame of a move, so it writes straight to the DOM instead of through signals. */
  private place(): void {
    const t = this.t;
    this.thumbs.forEach((el, i) => {
      const d = i - t;
      const far = Math.abs(d);
      // the chair in the big box has its own box tucked behind the frame; the rest queue up either side
      const x = far < 1 ? d * this.first : Math.sign(d) * (this.first + (far - 1) * this.pitch);
      el.style.transform = `translate3d(${x.toFixed(1)}px, 0, 0)`;
      el.style.opacity = (d >= 0 ? NEXT : d <= -1 ? DIM : NEXT + d * (NEXT - DIM)).toFixed(3);
    });
    this.slides.forEach((el, i) => {
      const d = i - t;
      el.style.transform = `translate3d(${(d * 100).toFixed(2)}%, 0, 0)`;
      el.style.visibility = Math.abs(d) < 1 ? 'visible' : 'hidden';
    });

    const index = Math.round(t);
    if (index !== this.active()) {
      this.active.set(index);
      this.priceOpen.set(false);
      this.show(index);
      void this.drawQr();
    }
    const settled = Math.abs(t - index) < 0.002;
    if (settled !== this.settled()) this.settled.set(settled);
  }

  /** Bring a chair to the big box by scrolling the page to its stop. */
  private scrollToStop(index: number, behavior: ScrollBehavior = 'smooth'): void {
    const steps = this.objects.length - 1;
    const { done, total } = this.travel();
    const stop = steps > 0 ? (total * Math.min(steps, Math.max(0, index))) / steps : 0;
    window.scrollTo({ top: window.scrollY + (stop - done), behavior: this.reducedMotion ? 'instant' : behavior });
  }

  /** A box was clicked. */
  protected goTo(index: number): void {
    // (the click that ends a drag is not a choice)
    if (performance.now() - this.draggedAt > 250) this.scrollToStop(index);
  }

  protected step(by: number): void {
    this.scrollToStop(this.active() + by);
  }

  /** The page has just scrolled: once it stops, see whether the strip was left between two chairs. */
  private planSnap(): void {
    clearTimeout(this.snapTimer);
    if (!this.reducedMotion) this.snapTimer = window.setTimeout(() => this.snap(), SNAP_AFTER);
  }

  /**
   * The scroll stopped part-way between two chairs: finish the move, so that
   * there is always a chair in the big box to turn. It carries on the way the
   * page was going, unless that had barely begun.
   */
  private snap(): void {
    if (this.touching || this.dragging() || this.destroyed) return;
    const steps = this.objects.length - 1;
    const { done, total } = this.travel();
    // (only while the section is stuck to the screen: on its way in or out the page scrolls freely)
    if (steps < 1 || done <= 0 || done >= total) return;
    const raw = (done / total) * steps;
    if (Math.abs(raw - Math.round(raw)) < 0.004) return;
    const index = Math.floor(raw);
    const part = raw - index;
    this.scrollToStop(index + (this.heading > 0 ? (part > SNAP_FROM ? 1 : 0) : part > 1 - SNAP_FROM ? 1 : 0));
  }

  protected onArrow(event: Event, by: number): void {
    // (inside the 3D view the arrow keys turn the model)
    if (event.target === this.viewer) return;
    event.preventDefault();
    this.step(by);
  }

  // ── dragging the strip ──────────────────────────────────────────────

  private readonly onDragStart = (e: PointerEvent): void => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    this.drag = { id: e.pointerId, x: e.clientX, moved: 0 };
  };

  private readonly onDragMove = (e: PointerEvent): void => {
    const drag = this.drag;
    if (!drag || e.pointerId !== drag.id) return;
    const dx = e.clientX - drag.x;
    drag.x = e.clientX;
    drag.moved += Math.abs(dx);
    // a few pixels of wobble is still a click on a box
    if (!this.dragging() && drag.moved > 6) {
      this.dragging.set(true);
      this.strip().nativeElement.setPointerCapture(e.pointerId);
    }
    if (!this.dragging()) return;
    // pulling the strip one box to the left is one step down the page
    const steps = this.objects.length - 1;
    const { total } = this.travel();
    if (steps > 0 && this.first > 0) window.scrollBy({ top: (-dx * total) / steps / this.first, behavior: 'instant' });
  };

  private readonly onDragEnd = (e: PointerEvent): void => {
    const drag = this.drag;
    if (!drag || e.pointerId !== drag.id) return;
    this.drag = null;
    if (!this.dragging()) return;
    this.dragging.set(false);
    const strip = this.strip().nativeElement;
    if (strip.hasPointerCapture(e.pointerId)) strip.releasePointerCapture(e.pointerId);
    // let go between two chairs: settle on the nearer one
    const steps = this.objects.length - 1;
    const { done, total } = this.travel();
    this.scrollToStop(Math.round(clamp01(done / total) * steps));
    this.draggedAt = performance.now();
  };

  // ── the 3D view ─────────────────────────────────────────────────────

  private async boot(): Promise<void> {
    try {
      await this.loadViewer();
    } catch {
      this.notice.set('The 3D viewer could not be loaded. Check the internet connection and reload the page.');
      return;
    }
    if (this.destroyed) return;

    const viewer = this.viewerRef().nativeElement;
    viewer.addEventListener('load', () => this.onModel());
    viewer.addEventListener('error', () => this.notice.set(MODEL_FAILED));
    viewer.addEventListener('camera-change', (e) => {
      if ((e as CustomEvent<{ source: string }>).detail.source === 'user-interaction') this.touched.set(true);
    });
    // a tap on the chair (not a drag that turned it) shows its price; a tap beside it puts the box away
    let down: { x: number; y: number; at: number } | null = null;
    viewer.addEventListener('pointerdown', (e) => (down = { x: e.clientX, y: e.clientY, at: performance.now() }));
    viewer.addEventListener('pointerup', (e) => {
      const start = down;
      down = null;
      if (!start || Math.hypot(e.clientX - start.x, e.clientY - start.y) > 6 || performance.now() - start.at > 600) return;
      // (the AR button sits on top of the viewer: pressing it is not a tap on the chair)
      if ((e.target as Element).closest('.ar__launch')) return;
      const onChair = !!viewer.positionAndNormalFromPoint(e.clientX, e.clientY);
      this.priceOpen.set(onChair && !this.priceOpen());
    });
    viewer.addEventListener('ar-status', (e) => {
      if ((e as CustomEvent<{ status: string }>).detail.status !== 'failed') return;
      this.notice.set(
        /android/i.test(navigator.userAgent)
          ? 'Google’s AR viewer could not open. Install or update “Google Play Services for AR” from the Play Store, then try again.'
          : 'AR could not start on this device. You can still turn the model here.',
      );
    });
    this.viewer = viewer;
    this.show(this.active());
  }

  private loadViewer(): Promise<unknown> {
    if (customElements.get('model-viewer')) return Promise.resolve();
    return new Promise<void>((resolve, reject) => {
      const script = document.createElement('script');
      script.type = 'module';
      script.src = VIEWER_SCRIPT;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('model-viewer did not load'));
      document.head.append(script);
    }).then(() => customElements.whenDefined('model-viewer'));
  }

  /** Hand the viewer a chair's model. The big box keeps its picture until the model has arrived. */
  private show(index: number): void {
    const viewer = this.viewer;
    if (!viewer || index === this.shown) return;
    const object = this.objects[index];
    this.shown = index;
    this.loaded.set(false);
    viewer.alt = `3D model: ${object.alt}`;
    if (object.iosModel) viewer.setAttribute('ios-src', object.iosModel);
    else viewer.removeAttribute('ios-src');
    this.aim();
    viewer.src = object.model;
  }

  private onModel(): void {
    const viewer = this.viewer;
    const object = this.objects[this.shown];
    // (a model that arrives after the strip has moved on is not the one to show)
    if (!viewer || !object || viewer.src !== object.model) return;
    this.loaded.set(true);
    if (this.notice() === MODEL_FAILED) this.notice.set('');
    if (this.arMode() === null) void this.describeAr();
    this.warm();
  }

  /** Point the camera at the angle the chair's picture was taken from. */
  private aim(): void {
    const viewer = this.viewer;
    const object = this.objects[this.shown];
    if (!viewer || !object) return;
    // model-viewer ignores an orbit equal to the last one it was given, even
    // after the visitor has dragged away from it — so alternate by a hair
    const nudge = (++this.resets % 2) * 0.001;
    const [theta, phi, radius] = object.orbit.split(' ');
    viewer.resetTurntableRotation(0);
    viewer.cameraOrbit = `${parseFloat(theta) + nudge}deg ${phi} ${radius}`;
    viewer.jumpCameraToGoal();
  }

  private onLive(live: boolean): void {
    const viewer = this.viewer;
    if (!viewer) return;
    clearTimeout(this.spinTimer);
    viewer.autoRotate = false;
    if (!live) return;
    this.aim();
    this.spinTimer = window.setTimeout(() => (viewer.autoRotate = this.spinning()), SPIN_AFTER);
  }

  /**
   * On a computer, fetch the other models quietly so that the next chair is
   * there the moment the strip stops. Phones skip this: it would spend mobile
   * data on chairs nobody has looked at.
   */
  private warm(): void {
    const saveData = (navigator as Navigator & { connection?: { saveData?: boolean } }).connection?.saveData;
    if (this.warmed || isPhone() || saveData) return;
    this.warmed = true;
    const run = () => {
      this.objects.forEach((object, i) => {
        if (i !== this.shown) void fetch(object.model).then((r) => r.blob()).catch(() => undefined);
      });
    };
    if ('requestIdleCallback' in window) requestIdleCallback(run);
    else setTimeout(run, 1500);
  }

  // ── AR ──────────────────────────────────────────────────────────────

  /** Work out how this device opens AR, and tell the visitor what to do. */
  private async describeAr(): Promise<void> {
    const mode = await detectArMode(!!this.objects[this.shown]?.iosModel);
    if (this.destroyed) return;
    this.arMode.set(mode);

    const phone = isPhone();
    if (mode === 'webxr') {
      this.arText.set('Tap “View in your room” and allow the camera. Point it at the floor and move the phone slowly until the chair appears.');
    } else if (mode === 'scene-viewer') {
      this.arText.set('Tap “View in your room” — it opens in Google’s AR viewer. Allow the camera there, then point it at the floor.');
    } else if (mode === 'quick-look') {
      this.arText.set('Tap “View in your room”, choose AR and allow the camera, then point it at the floor.');
    } else if (isIos() && IOS_OTHER_BROWSER.test(navigator.userAgent)) {
      this.arText.set('On an iPhone, AR starts from Safari. Open this page in Safari and tap “View in your room” there.');
    } else if (phone) {
      this.arText.set('This browser cannot start AR. Open this page in Chrome on Android, or in Safari on an iPhone.');
    } else {
      this.arText.set('Open this page on a phone or tablet to stand this chair in your own room.');
      await this.drawQr();
    }

    if (phone && !isSecureContext) {
      this.notice.set('This page is open on an http address. Phones only allow AR on https — open the page from its https address.');
    }
  }

  /**
   * On a computer: a QR code that opens this page, on this chair, on a phone.
   * Only when the page is on an address a phone can reach — not a file, and
   * not localhost.
   */
  private async drawQr(): Promise<void> {
    if (this.arMode() !== 'none' || isPhone()) return;
    const reachable = /^https?:$/.test(location.protocol) && !['localhost', '127.0.0.1', '[::1]', ''].includes(location.hostname);
    if (!reachable) return;
    const object = this.objects[this.active()];
    try {
      const { default: qrcode } = await import('qrcode-generator');
      const code = qrcode(0, 'M');
      code.addData(`${location.origin}${location.pathname}?ar=${object.id}#ar`);
      code.make();
      const size = code.getModuleCount();
      let path = '';
      for (let row = 0; row < size; row++) {
        for (let col = 0; col < size; col++) {
          if (code.isDark(row, col)) path += `M${col} ${row}h1v1h-1z`;
        }
      }
      if (this.destroyed || object !== this.objects[this.active()]) return;
      this.qr.set({ size, path });
      this.arText.set('Scan this code with your phone’s camera to stand this chair in your own room.');
    } catch {
      // no code: the text already says to open the page on a phone
    }
  }
}

/** iPhone browsers other than Safari: they cannot start AR from a model converted on the phone. */
const IOS_OTHER_BROWSER = /CriOS\/|EdgiOS\/|FxiOS\/|GSA\/|DuckDuckGo\//;

function isIos(): boolean {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

function isPhone(): boolean {
  return /android/i.test(navigator.userAgent) || isIos();
}

/**
 * Which AR route this device takes. Mirrors the order model-viewer tries them
 * in (its `ar-modes`): the camera inside the page first, then the phone's own
 * viewer app. The in-page camera (WebXR) exists only on https.
 */
async function detectArMode(hasIosModel: boolean): Promise<ArMode> {
  const ua = navigator.userAgent;
  const xr = (navigator as Navigator & { xr?: { isSessionSupported(mode: string): Promise<boolean> } }).xr;
  const hitTest = (self as unknown as { XRSession?: { prototype: { requestHitTestSource?: unknown } } }).XRSession?.prototype
    .requestHitTestSource;
  if (isSecureContext && xr && hitTest) {
    try {
      if (await xr.isSessionSupported('immersive-ar')) return 'webxr';
    } catch {
      // treated as not supported
    }
  }
  if (/android/i.test(ua) && !/firefox/i.test(ua) && !/OculusBrowser/.test(ua)) return 'scene-viewer';
  if (isIos()) {
    if (hasIosModel && !/GSA\//.test(ua)) return 'quick-look';
    if (!IOS_OTHER_BROWSER.test(ua) && document.createElement('a').relList?.supports?.('ar')) return 'quick-look';
  }
  return 'none';
}
